/* eslint-disable max-lines */
import { useAuth0 } from '@auth0/auth0-react';
import { skipToken } from '@reduxjs/toolkit/query';
import { formatDate } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { DollarAmount } from '@strata/react-dollar-amount';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { InlineMessage } from '@strata/react-inline-message';
import { FormTitleLabel, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { ClaimStatusIcon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { type ComponentProps, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { AuthDetails } from 'components/claims/AuthDetails';
import { ClaimHistory } from 'components/claims/ClaimHistory';
import { AcknowledgePaymentDiscrepancyDialog } from 'components/claims/dialogs/AcknowledgePaymentDiscrepancyDialog';
import { CheckClaimStatusDialog } from 'components/claims/dialogs/CheckClaimStatusDialog';
import { DocumentDenialReasonDialog } from 'components/claims/dialogs/DocumentDenialReasonDialog';
import { ResubmitClaimDialog } from 'components/claims/dialogs/ResubmitClaimDialog';
import { SnoozeResearchDialog } from 'components/claims/dialogs/SnoozeResearchDialog';
import { Update277CAStatusDialog } from 'components/claims/dialogs/Update277CAStatusDialog';
import { Update999StatusDialog } from 'components/claims/dialogs/Update999StatusDialog';
import { UpdateClaimStatusDialog } from 'components/claims/dialogs/UpdateClaimStatusDialog';
import { NotesTab } from 'components/claims/NotesTab';
import { OverviewTab } from 'components/claims/OverviewTab';
import { RawTransactionsTab } from 'components/claims/RawTransactionsTab';
import { ReferralDetails } from 'components/claims/ReferralDetails';
import { Service } from 'components/claims/Service';
import { ServicesOverview } from 'components/claims/ServicesOverview';
import { SnoozeStatusIcon } from 'components/shared/SnoozeStatusIcon';
import { Permissions } from 'services/constants';
import { AppRoutes } from 'services/routes';
import { useGetReferralDetailsQuery } from 'store/api/clientApi';
import {
    useGetClaimDetailsQuery,
    useGetClaimHistoryQuery,
    usePostUnsnoozeClaimMutation,
} from 'store/api/medbillingApi';
import { useGetPayersQuery } from 'store/api/referenceApi';
import { usePermissionCheck } from 'store/auth/authHelpers';

const ActionDialogs = {
    ACKNOWLEDGE_PAYMENT_DISCREPANCY: 'ACKNOWLEDGE_PAYMENT_DISCREPANCY',
    CHECK_CLAIM_STATUS: 'CHECK_CLAIM_STATUS',
    DOCUMENT_DENIAL_REASON: 'DOCUMENT_DENIAL_REASON',
    RESUBMIT_CLAIM: 'RESUBMIT_CLAIM',
    SNOOZE_RESEARCH: 'SNOOZE_RESEARCH',
    UPDATE_999_STATUS: 'UPDATE_999_STATUS',
    UPDATE_277_CA_STATUS: 'UPDATE_277_CA_STATUS',
    UPDATE_CLAIM_STATUS: 'UPDATE_CLAIM_STATUS',
} as const;
type ActionDialog = (typeof ActionDialogs)[keyof typeof ActionDialogs];

const ActionDialogComponentMap = {
    [ActionDialogs.ACKNOWLEDGE_PAYMENT_DISCREPANCY]: AcknowledgePaymentDiscrepancyDialog,
    [ActionDialogs.CHECK_CLAIM_STATUS]: CheckClaimStatusDialog,
    [ActionDialogs.DOCUMENT_DENIAL_REASON]: DocumentDenialReasonDialog,
    [ActionDialogs.RESUBMIT_CLAIM]: ResubmitClaimDialog,
    [ActionDialogs.SNOOZE_RESEARCH]: SnoozeResearchDialog,
    [ActionDialogs.UPDATE_999_STATUS]: Update999StatusDialog,
    [ActionDialogs.UPDATE_277_CA_STATUS]: Update277CAStatusDialog,
    [ActionDialogs.UPDATE_CLAIM_STATUS]: UpdateClaimStatusDialog,
} as const;

// eslint-disable-next-line complexity
export const ClaimDetailsPage = () => {
    const { payerId, transactionId } = useParams() as { payerId: string; transactionId: string };
    const navigate = useNavigate();
    const location = useLocation();
    const writeMedBilling = usePermissionCheck(Permissions.WRITE_MED_BILLING);

    const { user } = useAuth0();

    const { data: claimDetails, isFetching: claimDetailsLoading } = useGetClaimDetailsQuery({
        payerId,
        transactionId,
    });
    const { data: payers } = useGetPayersQuery();
    const { data: referralDetails, isFetching: referralDetailsLoading } =
        useGetReferralDetailsQuery(
            claimDetails?.referralId ? { payerId, referralId: claimDetails.referralId } : skipToken,
        );

    const { data: history, isFetching: historyLoading } = useGetClaimHistoryQuery({
        payerId,
        transactionId,
    });
    const mostRecentClaim = history?.[0];
    const hasBeenResubmitted = mostRecentClaim && mostRecentClaim.transactionId !== transactionId;

    const [actionDialogToShow, setActionDialogToShow] = useState<ActionDialog>();
    const ActiveActionDialog = actionDialogToShow && ActionDialogComponentMap[actionDialogToShow];
    const closeActionDialog = () => setActionDialogToShow(undefined);

    /** No actions available without write:medbilling or if claim was resubmitted */
    const hideActions =
        claimDetailsLoading || !claimDetails || !writeMedBilling || hasBeenResubmitted;

    const menuItems = (() => {
        if (hideActions) {
            return undefined;
        }

        const items: ComponentProps<typeof ButtonWithDropdownMenu>['menuItems'] = [];

        const addActionDialogMenuItem = (actionDialog: ActionDialog) => {
            items.push({
                text: ActionDialogComponentMap[actionDialog].Title,
                onItemClick: () => setActionDialogToShow(actionDialog),
            });
        };

        if (claimDetails.claimStatus === ClaimStatusIcon.Statuses.PAYDIS) {
            addActionDialogMenuItem(ActionDialogs.ACKNOWLEDGE_PAYMENT_DISCREPANCY);
        }

        addActionDialogMenuItem(ActionDialogs.CHECK_CLAIM_STATUS);

        if (claimDetails.claimStatus === ClaimStatusIcon.Statuses.DENIED) {
            addActionDialogMenuItem(ActionDialogs.DOCUMENT_DENIAL_REASON);
        }

        addActionDialogMenuItem(ActionDialogs.RESUBMIT_CLAIM);

        if (claimDetails.status999 !== ClaimStatusIcon.Statuses.ACCEPTED) {
            addActionDialogMenuItem(ActionDialogs.UPDATE_999_STATUS);
        } else if (claimDetails.status277CA !== ClaimStatusIcon.Statuses.ACCEPTED) {
            addActionDialogMenuItem(ActionDialogs.UPDATE_277_CA_STATUS);
        } else {
            addActionDialogMenuItem(ActionDialogs.UPDATE_CLAIM_STATUS);
        }

        return items;
    })();

    const [postUnsnoozeClaim] = usePostUnsnoozeClaimMutation();
    const additionalMenuItems = (() => {
        if (hideActions) {
            return undefined;
        }

        const items: ComponentProps<typeof ButtonWithDropdownMenu>['additionalMenuItems'] = [];

        if (!claimDetails.snoozeDate) {
            items.push({
                text: ActionDialogComponentMap[ActionDialogs.SNOOZE_RESEARCH].Title,
                onItemClick: () => setActionDialogToShow(ActionDialogs.SNOOZE_RESEARCH),
            });
        } else {
            items.push({
                text: 'Edit Snooze',
                onItemClick: () => setActionDialogToShow(ActionDialogs.SNOOZE_RESEARCH),
            });
            items.push({
                text: 'Unsnooze Research',
                onItemClick: () => {
                    (async () => {
                        await postUnsnoozeClaim({ payerId, transactionId }).unwrap();
                        SuccessToast({ message: 'Claim Research was Unsnoozed successfully' });
                    })();
                },
            });
        }

        return items;
    })();

    return (
        <FormHeaderAndTabbedContent
            rowOneContentHeader={
                <>
                    {ActiveActionDialog && (
                        <ActiveActionDialog
                            onClose={closeActionDialog}
                            payerId={payerId}
                            transactionId={transactionId}
                            username={user?.name}
                            referralDetails={referralDetails}
                        />
                    )}
                    <ActionButtonPageTitle
                        label="Claims"
                        title="Claim Details"
                        onClick={() => navigate(AppRoutes.claims)}
                    />
                    {menuItems && (
                        <div style={{ marginLeft: 'auto' }}>
                            <ButtonWithDropdownMenu
                                menuItems={menuItems}
                                additionalMenuItems={additionalMenuItems}
                                intent="primary"
                                label="Actions"
                            />
                        </div>
                    )}
                </>
            }
            rowTwoContentHeader={
                !claimDetailsLoading ? (
                    <>
                        <FormTitleLabel
                            label={
                                <div className="flex__gap--small">
                                    <SnoozeStatusIcon
                                        large
                                        snoozeDate={claimDetails?.snoozeDate}
                                        snoozeReason={claimDetails?.snoozeReason}
                                        snoozeFollowUpDate={claimDetails?.snoozeFollowUpDate}
                                    />
                                    <span>{claimDetails?.transactionId}</span>
                                </div>
                            }
                        />
                        <HeaderLabelValuePair label="Product" value={referralDetails?.product} />
                        <HeaderLabelValuePair
                            label="Submitted Amount"
                            value={
                                <DollarAmount
                                    amount={claimDetails?.submittedAmount}
                                    showZeroValue
                                    large
                                />
                            }
                        />
                        <HeaderLabelValuePair
                            label="Paid Amount"
                            value={
                                <DollarAmount
                                    amount={claimDetails?.claimTotal}
                                    showZeroValue
                                    large
                                />
                            }
                        />
                        <HeaderLabelValuePair
                            label="Health Plan"
                            value={payers?.find((payer) => payer.payerId === payerId)?.payerName}
                        />
                        <HeaderLabelValuePair
                            label="Submission Date"
                            value={formatDate(claimDetails?.submittedDate)}
                        />
                    </>
                ) : null
            }
            rowThreeContentHeader={
                !claimDetailsLoading &&
                hasBeenResubmitted && (
                    <InlineMessage
                        intent={InlineMessage.Intent.WARNING}
                        title="Resubmitted Claim"
                        label={
                            <>
                                <span>This claim was resubmitted, you can access the </span>
                                <u
                                    className="sds_claim-details__resubmitted-link"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                        navigate(
                                            AppRoutes.claimDetails(
                                                payerId,
                                                mostRecentClaim.transactionId,
                                            ),
                                        )
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            navigate(
                                                AppRoutes.claimDetails(
                                                    payerId,
                                                    mostRecentClaim.transactionId,
                                                ),
                                            );
                                        }
                                    }}
                                >
                                    most recent claim
                                </u>
                                <span> or view more details from the history tab.</span>
                            </>
                        }
                    />
                )
            }
            tabs={[
                {
                    title: 'Overview',
                    mainContent: (
                        <OverviewTab
                            claimDetails={claimDetails}
                            claimDetailsLoading={claimDetailsLoading}
                        />
                    ),
                },
                {
                    title: 'Services',
                    rowOneContent: (
                        <ServicesOverview
                            claimDetails={claimDetails}
                            claimDetailsLoading={claimDetailsLoading}
                        />
                    ),
                    rowTwoContent: (
                        <div style={{ width: '100%' }}>
                            {claimDetails?.services?.map((service, index) => (
                                <Service
                                    key={service.procedureCode}
                                    service={service}
                                    index={index}
                                    referralDetails={referralDetails}
                                />
                            ))}
                        </div>
                    ),
                },
                {
                    title: 'Referral',
                    rowOneContent: (
                        <ReferralDetails
                            claimDetails={claimDetails}
                            claimDetailsLoading={claimDetailsLoading}
                            referralDetails={referralDetails}
                            referralDetailsLoading={referralDetailsLoading}
                        />
                    ),
                },
                {
                    title: 'Auths',
                    rowOneContent: (
                        <AuthDetails
                            claimDetails={claimDetails}
                            claimDetailsLoading={claimDetailsLoading}
                        />
                    ),
                },
                {
                    title: 'Notes',
                    className: 'tabbed-grid-content',
                    mainContent: <NotesTab />,
                },
                { title: 'Raw Transactions', mainContent: <RawTransactionsTab /> },
                {
                    title: 'History',
                    rowOneContent:
                        !historyLoading && history && history.length > 0 ? (
                            <ClaimHistory history={history} />
                        ) : null,
                },
            ]}
            activeTab={location.hash.replace(/^#/, '').replace(/-/g, ' ')}
            onTabSelect={(tab) =>
                navigate({
                    ...location,
                    hash: tab.replace(/\s/g, '-').toLocaleLowerCase(),
                })
            }
        />
    );
};
